import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IntradayBatchMetadata } from "model/state"
import { CONTRIBUTION } from "connected-components/compare/position-sets"
import { COMPARISON_MODES, REBALANCED_MODES, SELECTED_MODE_VALUES, END_VS_START_MODES } from "../helpers/mode-constants"
import { MONTH_TO_DATE } from "helpers/dates"
import { SHORT } from "resources/position-sets/composition"
import { removeInstanceKey } from "./remove-instance-key"

// Typed loosely to avoid a large TS migration of the JS context file consumers.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any

export interface CoreState {
  // Persisted preference fields
  instrument?: AnyValue
  normalization: AnyValue
  selectedMode: AnyValue
  filter?: AnyValue
  base?: AnyValue
  baseOverride: AnyValue
  isBaseOverridden: boolean
  secondInstrument?: AnyValue
  secondInstrumentStatus?: AnyValue
  comparisonInstrument?: AnyValue
  period: AnyValue
  startDate: AnyValue
  endDate: AnyValue
  useLatestDate: boolean
  performanceStartDate: AnyValue
  performanceEndDate: AnyValue
  isEndVsStartDateComparison: boolean
  isIntraday: boolean
  // Runtime (not persisted, reset on page load)
  earliestDate: AnyValue
  latestDate: AnyValue
  isAiDrawerOpen: boolean
  intradayBatchMetadata: IntradayBatchMetadata | null
  intradayPricingAsOf: string | null
  isEndDateIntraday: boolean
  instrumentsMetadata: AnyValue
  // Slice meta
  isPreferencesLoaded: boolean
}

/** Keyed by dashboardId. Partial forces TS to narrow absent keys to undefined. */
export type CoreRootState = Partial<Record<string, CoreState>>

export const DEFAULT_CORE_STATE: CoreState = {
  normalization: CONTRIBUTION,
  selectedMode: SELECTED_MODE_VALUES.original,
  isAiDrawerOpen: false,
  period: MONTH_TO_DATE,
  startDate: null,
  endDate: null,
  earliestDate: null,
  latestDate: null,
  useLatestDate: true,
  performanceStartDate: null,
  performanceEndDate: null,
  isIntraday: false,
  isEndDateIntraday: false,
  intradayBatchMetadata: null,
  intradayPricingAsOf: null,
  isEndVsStartDateComparison: false,
  instrumentsMetadata: null,
  isBaseOverridden: false,
  baseOverride: null,
  isPreferencesLoaded: false,
}

export function getComparisonInstrumentMode(
  currentMode: AnyValue,
  currentComparisonInstrument: AnyValue,
  newComparisonInstrument: AnyValue,
  currentSecondInstrument: AnyValue,
): AnyValue {
  if (COMPARISON_MODES.includes(currentMode) && !newComparisonInstrument) {
    return currentSecondInstrument ? SELECTED_MODE_VALUES.rebalancedVsOriginal : SELECTED_MODE_VALUES.original
  }

  if (!currentComparisonInstrument && newComparisonInstrument) {
    if (newComparisonInstrument.isOptimization) {
      return currentSecondInstrument ? SELECTED_MODE_VALUES.optimizedVsRebalanced : SELECTED_MODE_VALUES.optimizedVsOriginal
    }
    return currentSecondInstrument ? SELECTED_MODE_VALUES.rebalancedVsComparison : SELECTED_MODE_VALUES.originalVsComparison
  }

  if (newComparisonInstrument?.isOptimization) {
    if (END_VS_START_MODES.includes(currentMode) || currentMode === SELECTED_MODE_VALUES.end) {
      return currentSecondInstrument ? SELECTED_MODE_VALUES.optimizedVsRebalanced : SELECTED_MODE_VALUES.optimizedVsOriginal
    }
    switch (currentMode) {
      case SELECTED_MODE_VALUES.start:
      case SELECTED_MODE_VALUES.comparison:
        return SELECTED_MODE_VALUES.optimized
      case SELECTED_MODE_VALUES.rebalancedVsComparison:
        return SELECTED_MODE_VALUES.optimizedVsRebalanced
      case SELECTED_MODE_VALUES.originalVsComparison:
        return SELECTED_MODE_VALUES.optimizedVsOriginal
      default:
        break
    }
  }

  if (currentComparisonInstrument?.isOptimization && newComparisonInstrument && !newComparisonInstrument.isOptimization) {
    switch (currentMode) {
      case SELECTED_MODE_VALUES.optimizedVsRebalanced:
        return SELECTED_MODE_VALUES.rebalancedVsComparison
      case SELECTED_MODE_VALUES.optimizedVsOriginal:
        return SELECTED_MODE_VALUES.originalVsComparison
      case SELECTED_MODE_VALUES.optimized:
        return SELECTED_MODE_VALUES.comparison
      default:
        break
    }
  }

  return currentMode
}

export function getSecondInstrumentMode(
  currentMode: AnyValue,
  currentSecondInstrument: AnyValue,
  newSecondInstrument: AnyValue,
  currentComparisonInstrument: AnyValue,
  overrideMode: AnyValue = null,
): AnyValue {
  if (overrideMode) {
    return overrideMode
  }
  if (REBALANCED_MODES.includes(currentMode) && !newSecondInstrument) {
    if (!currentComparisonInstrument) {
      return SELECTED_MODE_VALUES.original
    }
    return currentComparisonInstrument.isOptimization
      ? SELECTED_MODE_VALUES.optimizedVsOriginal
      : SELECTED_MODE_VALUES.originalVsComparison
  }
  if (!currentSecondInstrument && newSecondInstrument) {
    if (!currentComparisonInstrument) {
      return SELECTED_MODE_VALUES.rebalanced
    }
    return currentComparisonInstrument.isOptimization
      ? SELECTED_MODE_VALUES.optimizedVsRebalanced
      : SELECTED_MODE_VALUES.rebalancedVsComparison
  }
  return currentMode
}

export function getEndVsStartInstrumentMode(
  currentMode: AnyValue,
  currentSecondInstrument: AnyValue,
  newSecondInstrument: AnyValue,
  currentComparisonInstrument: AnyValue,
): AnyValue {
  if (END_VS_START_MODES.includes(currentMode) && !newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.endVsStart : SELECTED_MODE_VALUES.end
  }
  if (!currentSecondInstrument && newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.rebalancedVsEnd : SELECTED_MODE_VALUES.rebalanced
  }
  return currentMode
}

function getInstrumentWeight(mainInstrumentMetadata: AnyValue): number {
  return mainInstrumentMetadata?.compositionType === SHORT ? -1 : 1
}

function ensureInstance(state: CoreRootState, dashboardId: string): CoreState {
  if (!state[dashboardId]) {
    state[dashboardId] = { ...DEFAULT_CORE_STATE }
  }
  return state[dashboardId] as CoreState
}

// Canonical list of CoreState fields persisted via `usePreferences`. Imported by
// `use-sync-core-preferences.ts` as the preference-fields array, and used here
// (as a Set) to filter unknown backend keys during schema migrations.
export const CORE_PREFERENCE_FIELDS: string[] = [
  "instrument", "normalization", "selectedMode", "filter", "base", "baseOverride",
  "isBaseOverridden", "secondInstrument", "comparisonInstrument",
  "period", "startDate", "endDate", "useLatestDate", "performanceStartDate",
  "performanceEndDate", "isEndVsStartDateComparison",
]

const CORE_PREFERENCE_FIELDS_SET = new Set<string>(CORE_PREFERENCE_FIELDS)

export const coreSlice = createSlice({
  name: "dashboard/core",
  initialState: {} as CoreRootState,
  reducers: {
    setPreferences(
      state,
      action: PayloadAction<{ dashboardId: string } & Record<string, AnyValue>>,
    ) {
      const { dashboardId, ...prefs } = action.payload
      const instance = ensureInstance(state, dashboardId)
      // Filter to only known preference fields before applying — prevents unknown keys
      // from backend schema migrations silently polluting the instance.
      for (const key of Object.keys(prefs)) {
        if (CORE_PREFERENCE_FIELDS_SET.has(key)) {
          (instance as Record<string, AnyValue>)[key] = prefs[key]
        }
      }
      instance.isPreferencesLoaded = true
    },

    setInstrument(
      state,
      action: PayloadAction<{ dashboardId: string, instrument: AnyValue }>,
    ) {
      const { dashboardId, instrument } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.instrument = instrument
      instance.intradayBatchMetadata = null
      instance.intradayPricingAsOf = null
      instance.filter = null
    },

    setPeriod(
      state,
      action: PayloadAction<{ dashboardId: string, period: AnyValue }>,
    ) {
      const { dashboardId, period } = action.payload
      const instance = ensureInstance(state, dashboardId)
      if (period?.startDate && period?.endDate) {
        instance.startDate = period.startDate
        instance.endDate = period.endDate
        if (period.period) {
          instance.period = period.period
        } else if (!instance.period) {
          instance.period = "CUSTOM"
        }
      } else if (typeof period === "string") {
        instance.period = period
      }
    },

    setBoundaryDates(
      state,
      action: PayloadAction<{ dashboardId: string, earliestDate: AnyValue, latestDate: AnyValue }>,
    ) {
      const { dashboardId, earliestDate, latestDate } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.earliestDate = earliestDate
      instance.latestDate = latestDate
    },

    setUseLatestDate(
      state,
      action: PayloadAction<{ dashboardId: string, useLatestDate: boolean }>,
    ) {
      const { dashboardId, useLatestDate } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.useLatestDate = useLatestDate
    },

    setPerformanceStartDate(
      state,
      action: PayloadAction<{ dashboardId: string, performanceStartDate: AnyValue }>,
    ) {
      const { dashboardId, performanceStartDate } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.performanceStartDate = performanceStartDate
    },

    setPerformanceEndDate(
      state,
      action: PayloadAction<{ dashboardId: string, performanceEndDate: AnyValue }>,
    ) {
      const { dashboardId, performanceEndDate } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.performanceEndDate = performanceEndDate
    },

    setBase(
      state,
      action: PayloadAction<{ dashboardId: string, base: AnyValue, mainInstrumentMetadata?: AnyValue }>,
    ) {
      const { dashboardId, base, mainInstrumentMetadata } = action.payload
      const instance = ensureInstance(state, dashboardId)
      if (base) {
        instance.base = { ...base, weight: getInstrumentWeight(mainInstrumentMetadata) }
      } else {
        instance.base = base
      }
    },

    setBaseOverride(
      state,
      action: PayloadAction<{ dashboardId: string, baseOverride: AnyValue }>,
    ) {
      const { dashboardId, baseOverride } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.baseOverride = baseOverride
    },

    setIsBaseOverridden(
      state,
      action: PayloadAction<{ dashboardId: string, isBaseOverridden: boolean }>,
    ) {
      const { dashboardId, isBaseOverridden } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isBaseOverridden = isBaseOverridden
    },

    setSecondInstrument(
      state,
      action: PayloadAction<{
        dashboardId: string
        secondInstrument: AnyValue
        secondInstrumentStatus?: AnyValue
        overrideMode?: AnyValue
      }>,
    ) {
      const { dashboardId, secondInstrument, secondInstrumentStatus, overrideMode = null } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.selectedMode = instance.isEndVsStartDateComparison
        ? getEndVsStartInstrumentMode(instance.selectedMode, instance.secondInstrument, secondInstrument, instance.comparisonInstrument)
        : getSecondInstrumentMode(instance.selectedMode, instance.secondInstrument, secondInstrument, instance.comparisonInstrument, overrideMode)
      instance.secondInstrument = secondInstrument
      instance.secondInstrumentStatus = secondInstrumentStatus
    },

    setComparisonInstrument(
      state,
      action: PayloadAction<{ dashboardId: string, instrument: AnyValue }>,
    ) {
      const { dashboardId, instrument } = action.payload
      const instance = ensureInstance(state, dashboardId)
      const exitingEndVsStartForOptimization = Boolean(instrument?.isOptimization) && instance.isEndVsStartDateComparison
      if (exitingEndVsStartForOptimization) {
        instance.selectedMode = getComparisonInstrumentMode(instance.selectedMode, instance.comparisonInstrument, instrument, instance.secondInstrument)
        instance.isEndVsStartDateComparison = false
      } else if (instance.isEndVsStartDateComparison) {
        instance.selectedMode = getEndVsStartInstrumentMode(instance.selectedMode, instance.secondInstrument, instance.secondInstrument, instrument)
      } else {
        instance.selectedMode = getComparisonInstrumentMode(instance.selectedMode, instance.comparisonInstrument, instrument, instance.secondInstrument)
      }
      instance.comparisonInstrument = instrument
    },

    setIsEndVsStartDateComparison(
      state,
      action: PayloadAction<{ dashboardId: string, isEndVsStartDateComparison: boolean }>,
    ) {
      const { dashboardId, isEndVsStartDateComparison } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isEndVsStartDateComparison = isEndVsStartDateComparison
    },

    setFilter(
      state,
      action: PayloadAction<{ dashboardId: string, filter: AnyValue }>,
    ) {
      const { dashboardId, filter } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.filter = filter
    },

    setNormalization(
      state,
      action: PayloadAction<{ dashboardId: string, normalization: AnyValue }>,
    ) {
      const { dashboardId, normalization } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.normalization = normalization
    },

    setSelectedMode(
      state,
      action: PayloadAction<{ dashboardId: string, selectedMode: AnyValue }>,
    ) {
      const { dashboardId, selectedMode } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.selectedMode = selectedMode
    },

    setIsAiDrawerOpen(
      state,
      action: PayloadAction<{ dashboardId: string, isAiDrawerOpen: boolean }>,
    ) {
      const { dashboardId, isAiDrawerOpen } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isAiDrawerOpen = isAiDrawerOpen
    },

    setIsIntraday(
      state,
      action: PayloadAction<{ dashboardId: string, isIntraday: boolean }>,
    ) {
      const { dashboardId, isIntraday } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isIntraday = isIntraday
      if (!isIntraday) {
        instance.intradayBatchMetadata = null
        instance.intradayPricingAsOf = null
      }
    },

    setIsEndDateIntraday(
      state,
      action: PayloadAction<{ dashboardId: string, isEndDateIntraday: boolean }>,
    ) {
      const { dashboardId, isEndDateIntraday } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isEndDateIntraday = isEndDateIntraday
    },

    setIntradayBatchMetadata(
      state,
      action: PayloadAction<{ dashboardId: string, intradayBatchMetadata: IntradayBatchMetadata | null }>,
    ) {
      const { dashboardId, intradayBatchMetadata } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.intradayBatchMetadata = intradayBatchMetadata
    },

    setIntradayPricingAsOf(
      state,
      action: PayloadAction<{ dashboardId: string, intradayPricingAsOf: string | null }>,
    ) {
      const { dashboardId, intradayPricingAsOf } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.intradayPricingAsOf = intradayPricingAsOf
    },

    setInstrumentsMetadata(
      state,
      action: PayloadAction<{ dashboardId: string, instrumentsMetadata: AnyValue }>,
    ) {
      const { dashboardId, instrumentsMetadata } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.instrumentsMetadata = instrumentsMetadata
    },

    // Drops the per-dashboard entry on delete (no unmount GC, else it leaks).
    removeInstance(state, action: PayloadAction<{ dashboardId: string }>) {
      return removeInstanceKey(state, action.payload.dashboardId)
    },
  },
})

export const { removeInstance } = coreSlice.actions
export default coreSlice.reducer
